import type { DataSource } from "@estelle/domain";

import { createApiCatalog } from "@/adapters/api/catalog";
import { createApiComments } from "@/adapters/api/comments";
import { createApiContacts } from "@/adapters/api/contacts";
import { createApiCustomers } from "@/adapters/api/customers";
import { createApiDashboard } from "@/adapters/api/dashboard";
import { createApiDocs } from "@/adapters/api/docs";
import { createApiEscalations } from "@/adapters/api/escalations";
import { createApiInventory } from "@/adapters/api/inventory";
import { createApiInvoicing } from "@/adapters/api/invoicing";
import { createApiNotifications } from "@/adapters/api/notifications";
import { createApiOrders } from "@/adapters/api/orders";
import { createApiReturns } from "@/adapters/api/returns";
import { createApiStages } from "@/adapters/api/stages";
import { createApiSavedViews } from "@/adapters/api/savedViews";
import { createApiSettings } from "@/adapters/api/settings";
import { createApiTasks } from "@/adapters/api/tasks";
import { createApiTracking } from "@/adapters/api/tracking";
import { createApiWarehouses } from "@/adapters/api/warehouses";
import { UnauthorizedError } from "@/adapters/api/base";
import { refreshCurrentUser } from "@/auth/backend";

/**
 * The real data source, one module at a time.
 *
 * Catalog and order reads, saved views, stock levels, low stock, stock
 * adjustments (#171), the warehouse master, stock transfers and In Transit
 * (#172), contacts (M12-4), docs (#381) and tasks (#160) come from
 * Postgres. Invoicing is the one repository without routes yet (GST is
 * postponed, M12-6), and it is `createApiInvoicing`'s empty answers rather
 * than the fixture's: this file imports nothing from `adapters/fixture`,
 * because a build with `VITE_API_URL` set ships no fixture code (#163).
 * Composing module by module is what lets one cross over the moment its API
 * routes exist, without the other tracks waiting for it — and no screen
 * changes either way, because every screen already talks to the interface
 * rather than to an implementation.
 */
export function createApiDataSource(): DataSource {
  return {
    /*
      The signed-in account, from the session cookie (M10-2). Everything that
      shows a name, and the permissions every screen decides by, reads this — so
      it has to be the real identity rather than the fixture's, the moment there
      is one, and it has to be read afresh each time React Query asks. A
      permission granted while a page is open reaches that page through here
      (#498); answered from the page's first read it would wait for a reload.
    */
    currentUser: async () => {
      const user = await refreshCurrentUser();
      if (!user) throw new UnauthorizedError("/api/me");
      return user;
    },
    catalog: createApiCatalog(),
    orders: createApiOrders(),
    inventory: createApiInventory(),
    warehouses: createApiWarehouses(),
    contacts: createApiContacts(),
    customers: createApiCustomers(),
    comments: createApiComments(),
    docs: createApiDocs(),
    tasks: createApiTasks(),
    savedViews: createApiSavedViews(),
    notifications: createApiNotifications(),
    settings: createApiSettings(),
    tracking: createApiTracking(),
    returns: createApiReturns(),
    escalations: createApiEscalations(),
    stages: createApiStages(),
    dashboard: createApiDashboard(),
    /*
      GST is postponed (M12-6), so `invoicing` answers with nothing rather
      than with data — see `createApiInvoicing`. This flag is what tells the
      dashboard's uninvoiced tile not to show a count nobody can act on; flip
      it the same day `invoicing:` below gains a real implementation.
    */
    invoicing: createApiInvoicing(),
    invoicingIsLive: false,
  };
}
